<script setup lang="ts">
import { computed, ref } from 'vue';
import { t } from '../i18n';
import { pinSet } from '../profile-card';
import { checkPin, hashPin, PIN_RE } from '../profiles/pin';
import { LOCK_AFTER, updateProfile, type LockAfter } from '../profiles/registry';
import { lockProfile, restartIdle } from '../profiles/lock';
import { profileState } from '../profiles/state';
import { useApp } from '../stores/app';

/**
 * The open profile's PIN (set, change, remove — the current one is asked to change or remove it),
 * auto-lock after idle time and "Lock now" (docs/TZ-PROFILES.md 4.5).
 */
const emit = defineEmits<{ close: [] }>();
const app = useApp();

type Mode = '' | 'set' | 'change' | 'remove';
const mode = ref<Mode>('');
const current = ref('');
const next = ref('');
const repeat = ref('');
const error = ref('');
const busy = ref(false);

const needsCurrent = computed(() => mode.value === 'change' || mode.value === 'remove');
const needsNew = computed(() => mode.value === 'set' || mode.value === 'change');
const ready = computed(() => (!needsCurrent.value || PIN_RE.test(current.value)) && (!needsNew.value || (PIN_RE.test(next.value) && next.value === repeat.value)));
const lockAfter = computed(() => profileState.current?.lockAfter ?? 0);

function start(m: Mode): void {
  mode.value = m;
  current.value = next.value = repeat.value = error.value = '';
}
const digits = (s: string) => s.replace(/\D/g, '');

async function apply(): Promise<void> {
  const p = profileState.current;
  if (!p || !ready.value || busy.value) return;
  busy.value = true;
  error.value = '';
  try {
    if (needsCurrent.value && !(p.pin && (await checkPin(current.value, p.pin)))) {
      error.value = t('profiles.pinCurrentWrong');
      return;
    }
    const pin = mode.value === 'remove' ? null : await hashPin(next.value);
    profileState.current = await updateProfile(p.id, { pin, failed: 0, lockedUntil: 0, ...(pin ? {} : { lockAfter: 0 }) });
    pinSet.value = !!pin;
    app.notify('info', mode.value === 'remove' ? t('profiles.pinRemoved') : t('profiles.pinSaved'));
    mode.value = '';
    restartIdle();
  } finally {
    busy.value = false;
  }
}

async function setLockAfter(v: LockAfter): Promise<void> {
  const p = profileState.current;
  if (!p) return;
  profileState.current = await updateProfile(p.id, { lockAfter: v });
  restartIdle();
}

function lockNow(): void {
  emit('close');
  lockProfile();
}
</script>

<template>
  <div class="sec" data-dialog="profile-security">
    <p class="note">{{ t('profiles.pinNote') }}</p>
    <div v-if="!mode" class="row-actions">
      <button v-if="!pinSet" type="button" class="pill" data-act="pin-set" @click="start('set')">{{ t('profile.pinSet') }}</button>
      <template v-else>
        <button type="button" class="pill" data-act="pin-change" @click="start('change')">{{ t('profile.pinChange') }}</button>
        <button type="button" class="pill danger" data-act="pin-remove" @click="start('remove')">{{ t('profile.pinDelete') }}</button>
        <button type="button" class="pill" data-act="lock-now" @click="lockNow">{{ t('profiles.lockNow') }}</button>
      </template>
    </div>
    <form v-else class="pin-edit" @submit.prevent="apply">
      <input v-if="needsCurrent" v-model="current" class="input" type="password" inputmode="numeric" maxlength="8" autocomplete="current-password" :placeholder="t('profiles.pinCurrent')" :aria-label="t('profiles.pinCurrent')" data-field="pin-current" @input="current = digits(current)" />
      <template v-if="needsNew">
        <input v-model="next" class="input" type="password" inputmode="numeric" maxlength="8" autocomplete="new-password" :placeholder="t('profiles.pinNew')" :aria-label="t('profiles.pinNew')" data-field="pin-new" @input="next = digits(next)" />
        <input v-model="repeat" class="input" type="password" inputmode="numeric" maxlength="8" autocomplete="new-password" :placeholder="t('profiles.pinRepeat')" :aria-label="t('profiles.pinRepeat')" data-field="pin-repeat" @input="repeat = digits(repeat)" />
        <p v-if="repeat && next !== repeat" class="note warn">{{ t('profiles.pinMismatch') }}</p>
      </template>
      <p v-if="error" class="note warn" role="alert">{{ error }}</p>
      <div class="row-actions">
        <button type="button" class="pill" @click="mode = ''">{{ t('common.cancel') }}</button>
        <button type="submit" class="pill primary" :disabled="!ready || busy" data-act="pin-apply">{{ mode === 'remove' ? t('profile.pinDelete') : t('common.save') }}</button>
      </div>
    </form>

    <template v-if="pinSet">
      <div class="lock-row">
        <span class="label">{{ t('profiles.autoLock') }}</span>
        <div class="seg-pills" role="radiogroup" :aria-label="t('profiles.autoLock')">
          <button v-for="v in LOCK_AFTER" :key="v" type="button" role="radio" :aria-checked="lockAfter === v" :class="{ active: lockAfter === v }" :data-lock-after="v" @click="setLockAfter(v)">
            {{ v ? t('profiles.minutes', { n: v }) : t('profiles.autoLockOff') }}
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.sec {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
}
.note {
  margin: 0;
  font-size: 12.5px;
  color: var(--text-2);
  line-height: 1.45;
}
.warn {
  color: var(--warn);
}
.row-actions {
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
}
.pin-edit {
  display: grid;
  gap: 8px;
  width: 100%;
}
.input {
  height: 38px;
  padding: 0 14px;
  border: 1px solid var(--border);
  border-radius: var(--radius-control);
  background: var(--bg);
  color: var(--text);
  font: inherit;
}
.input:focus {
  outline: none;
  border-color: var(--accent);
}
.lock-row {
  display: grid;
  gap: 8px;
}
.label {
  font-size: 13px;
  font-weight: 550;
}
</style>
